import { useEffect, useState, useCallback } from "react";
import { api, apiError } from "../lib/api";
import { toast } from "sonner";
import { TrendingUp, BarChart3, DollarSign, Lightbulb, RefreshCw, Search, ArrowUpRight, ArrowDownRight, Calculator, Globe } from "lucide-react";

const STATUS_STYLE = {
  draft: "bg-zinc-100 text-zinc-700",
  active: "bg-emerald-100 text-emerald-800",
  approved: "bg-blue-100 text-blue-800",
  archived: "bg-amber-100 text-amber-800",
};

export default function AICostIntelligence() {
  const [costSheets, setCostSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [benchmarking, setBenchmarking] = useState({});
  const [benchmarks, setBenchmarks] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [calculatingLanded, setCalculatingLanded] = useState({});
  const [landedResults, setLandedResults] = useState({});
  const [summary, setSummary] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/costs").then((r) => setCostSheets(r.data)),
      api.get("/costs/intelligence/summary").then((r) => setSummary(r.data)).catch(() => {}),
    ])
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const calculateLanded = async (id) => {
    setCalculatingLanded((prev) => ({ ...prev, [id]: true }));
    try {
      const r = await api.post(`/costs/${id}/calculate-landed`);
      setLandedResults((prev) => ({ ...prev, [id]: r.data }));
      toast.success("Landed cost calculated");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCalculatingLanded((prev) => ({ ...prev, [id]: false }));
    }
  };

  const runBenchmark = async (id) => {
    setBenchmarking((prev) => ({ ...prev, [id]: true }));
    try {
      const r = await api.post(`/costs/${id}/benchmark`);
      setBenchmarks((prev) => ({ ...prev, [id]: r.data }));
      toast.success("Benchmark analysis complete");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBenchmarking((prev) => ({ ...prev, [id]: false }));
    }
  };

  const totalSheets = costSheets.length;
  const avgCost = totalSheets > 0
    ? costSheets.reduce((sum, cs) => sum + (cs.landed_cost || 0), 0) / totalSheets
    : 0;
  const statusCounts = costSheets.reduce((acc, cs) => {
    const s = cs.status || "draft";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  const totalCost = costSheets.reduce((sum, cs) => sum + (cs.landed_cost || 0), 0);

  const filteredSheets = costSheets.filter((cs) => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (cs.style_name || "").toLowerCase().includes(q)
      || (cs.style_number || "").toLowerCase().includes(q);
  });

  const suggestions = [
    { title: "Consolidate fabric sourcing", description: "3 styles use similar fabrics from different suppliers. Consolidating could save 8-12% on material costs.", impact: "high" },
    { title: "Negotiate volume discounts", description: "Styles with order quantities above 500 units qualify for tier-2 pricing from current suppliers.", impact: "medium" },
    { title: "Optimize trim selections", description: "Replace premium zippers with equivalent-quality alternatives to reduce per-unit trim cost by $0.40.", impact: "medium" },
    { title: "Review shipping routes", description: "Switching to consolidated sea freight for non-urgent orders could reduce logistics costs by 15-20%.", impact: "high" },
  ];

  return (
    <div className="p-10 max-w-6xl mx-auto">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-zinc-400">PLM</p>
          <h1 className="font-heading text-3xl font-bold mt-2 tracking-tight">AI Cost Intelligence</h1>
          <p className="text-zinc-500 mt-2 text-sm">AI-powered cost benchmarking, trend analysis and optimization suggestions.</p>
        </div>
        <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-2 bg-zinc-950 text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-zinc-800 transition-colors disabled:opacity-40">
          <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Tabs */}
      <div className="mt-8 flex gap-1 border-b border-zinc-200">
        {[
          { id: "overview", label: "Overview", icon: BarChart3 },
          { id: "benchmarks", label: "Benchmarks", icon: TrendingUp },
          { id: "landed", label: "Landed Cost", icon: Calculator },
          { id: "trends", label: "Trends", icon: DollarSign },
        ].map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${tab === t.id ? "border-zinc-950 text-zinc-950" : "border-transparent text-zinc-400 hover:text-zinc-600"}`}>
            <t.icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="mt-10 text-sm text-zinc-400">Loading...</div>
      ) : tab === "overview" ? (
        <>
          {/* Summary Cards */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="border border-zinc-200 bg-white rounded-lg p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Total Cost Sheets</p>
              <p className="text-2xl font-bold mt-1">{totalSheets}</p>
            </div>
            <div className="border border-zinc-200 bg-white rounded-lg p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Average Landed Cost</p>
              <p className="text-2xl font-bold mt-1">${avgCost.toFixed(2)}</p>
            </div>
            <div className="border border-zinc-200 bg-white rounded-lg p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Total Cost Value</p>
              <p className="text-2xl font-bold mt-1">${totalCost.toFixed(2)}</p>
            </div>
            <div className="border border-zinc-200 bg-white rounded-lg p-5">
              <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Active Sheets</p>
              <p className="text-2xl font-bold mt-1">{statusCounts.active || 0}</p>
            </div>
          </div>

          {/* Status Distribution */}
          {Object.keys(statusCounts).length > 0 && (
            <div className="mt-6 border border-zinc-200 bg-white rounded-lg p-5">
              <h3 className="text-sm font-semibold mb-4">Cost Sheet Status Distribution</h3>
              <div className="flex gap-3 flex-wrap">
                {Object.entries(statusCounts).map(([status, count]) => (
                  <div key={status} className="flex items-center gap-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[status] || STATUS_STYLE.draft}`}>{status}</span>
                    <span className="text-sm text-zinc-600">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Optimization Suggestions */}
          <div className="mt-6">
            <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2">
              <Lightbulb size={15} className="text-amber-500" /> Cost Optimization Suggestions
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {suggestions.map((s, i) => (
                <div key={i} className="border border-zinc-200 bg-white rounded-lg p-5">
                  <div className="flex items-start justify-between">
                    <p className="text-sm font-medium">{s.title}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${s.impact === "high" ? "bg-emerald-100 text-emerald-800" : "bg-blue-100 text-blue-800"}`}>{s.impact} impact</span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-2">{s.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Cost Sheets Table */}
          {costSheets.length > 0 && (
            <div className="mt-8 border border-zinc-200 bg-white rounded-lg overflow-hidden">
              <div className="px-6 py-3 border-b border-zinc-100 flex items-center justify-between">
                <h3 className="text-sm font-semibold">All Cost Sheets</h3>
                <span className="text-xs text-zinc-400">{costSheets.length} sheets</span>
              </div>
              <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                <span className="col-span-3">Style</span>
                <span className="col-span-2">Number</span>
                <span className="col-span-2">Landed Cost</span>
                <span className="col-span-2">Wholesale</span>
                <span className="col-span-1">Retail</span>
                <span className="col-span-2">Status</span>
              </div>
              {costSheets.slice(0, 10).map((cs) => (
                <div key={cs.id} className="grid grid-cols-12 items-center px-6 py-3 border-b border-zinc-50">
                  <span className="col-span-3 text-sm font-medium truncate">{cs.style_name}</span>
                  <span className="col-span-2 text-sm text-zinc-600">{cs.style_number}</span>
                  <span className="col-span-2 text-sm text-zinc-600">{cs.currency} {(cs.landed_cost || 0).toFixed(2)}</span>
                  <span className="col-span-2 text-sm text-zinc-600">{cs.currency} {(cs.wholesale_price || 0).toFixed(2)}</span>
                  <span className="col-span-1 text-sm text-zinc-600">{cs.currency} {(cs.retail_price || 0).toFixed(2)}</span>
                  <span className="col-span-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[cs.status] || STATUS_STYLE.draft}`}>{cs.status || "draft"}</span>
                  </span>
                </div>
              ))}
            </div>
          )}
        </>
      ) : tab === "landed" ? (
        <>
          {/* Intelligence Summary */}
          {summary && (
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              {summary.total_sheets != null && (
                <div className="border border-zinc-200 bg-white rounded-lg p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Total Sheets</p>
                  <p className="text-2xl font-bold mt-1">{summary.total_sheets}</p>
                </div>
              )}
              {summary.avg_landed_cost != null && (
                <div className="border border-zinc-200 bg-white rounded-lg p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Avg Landed Cost</p>
                  <p className="text-2xl font-bold mt-1">${parseFloat(summary.avg_landed_cost).toFixed(2)}</p>
                </div>
              )}
              {summary.total_duty != null && (
                <div className="border border-zinc-200 bg-white rounded-lg p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Total Duty</p>
                  <p className="text-2xl font-bold mt-1">${parseFloat(summary.total_duty).toFixed(2)}</p>
                </div>
              )}
              {summary.total_import_vat != null && (
                <div className="border border-zinc-200 bg-white rounded-lg p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Total Import VAT</p>
                  <p className="text-2xl font-bold mt-1">${parseFloat(summary.total_import_vat).toFixed(2)}</p>
                </div>
              )}
            </div>
          )}

          {/* Landed Cost Calculator */}
          <div className="mt-6">
            <h3 className="font-heading font-semibold text-sm mb-3 flex items-center gap-2">
              <Globe size={15} className="text-zinc-500" /> Landed Cost Calculator
            </h3>
            {filteredSheets.length === 0 ? (
              <div className="border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
                <Calculator size={26} className="mx-auto mb-3" />
                <p className="text-sm">No cost sheets available for landed cost calculation.</p>
              </div>
            ) : (
              <div className="border border-zinc-200 bg-white rounded-lg overflow-hidden">
                <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                  <span className="col-span-3">Style</span>
                  <span className="col-span-2">Landed Cost</span>
                  <span className="col-span-2">Customs Value</span>
                  <span className="col-span-2">Duty</span>
                  <span className="col-span-1">VAT</span>
                  <span className="col-span-2 text-right">Action</span>
                </div>
                {filteredSheets.map((cs) => {
                  const lr = landedResults[cs.id] || cs;
                  return (
                    <div key={cs.id} className="grid grid-cols-12 items-center px-6 py-3 border-b border-zinc-50">
                      <div className="col-span-3">
                        <p className="text-sm font-medium truncate">{cs.style_name}</p>
                        <p className="text-[10px] text-zinc-400">{cs.style_number}</p>
                      </div>
                      <span className="col-span-2 text-sm text-zinc-600">{cs.currency} {(lr.landed_cost || cs.landed_cost || 0).toFixed(2)}</span>
                      <span className="col-span-2 text-sm text-zinc-600">{lr.customs_value != null ? `${cs.currency} ${parseFloat(lr.customs_value).toFixed(2)}` : "-"}</span>
                      <span className="col-span-2 text-sm text-zinc-600">{lr.duty_amount != null ? `${cs.currency} ${parseFloat(lr.duty_amount).toFixed(2)}` : "-"}</span>
                      <span className="col-span-1 text-sm text-zinc-600">{lr.import_vat != null ? `${cs.currency} ${parseFloat(lr.import_vat).toFixed(2)}` : "-"}</span>
                      <span className="col-span-2 text-right">
                        <button onClick={() => calculateLanded(cs.id)} disabled={calculatingLanded[cs.id]}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-40 transition-colors">
                          {calculatingLanded[cs.id] ? <><RefreshCw size={12} className="animate-spin" /> Calculating...</> : <><Calculator size={12} /> Calculate</>}
                        </button>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      ) : tab === "benchmarks" ? (
        <>
          {/* Search */}
          <div className="mt-6 flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
              <input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search cost sheets..."
                className="w-full pl-9 pr-3 py-2.5 border border-zinc-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-zinc-950" />
            </div>
          </div>

          {filteredSheets.length === 0 ? (
            <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
              <TrendingUp size={26} className="mx-auto mb-3" />
              <p className="text-sm">No cost sheets found.</p>
              <p className="text-xs mt-1">Cost sheets created from techpacks will appear here for benchmarking.</p>
            </div>
          ) : (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {filteredSheets.map((cs) => (
                <div key={cs.id} className="border border-zinc-200 bg-white rounded-lg p-5">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm font-medium">{cs.style_name}</p>
                      <p className="text-xs text-zinc-400 mt-0.5">{cs.style_number} | {cs.currency} {(cs.landed_cost || 0).toFixed(2)} landed</p>
                    </div>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLE[cs.status] || STATUS_STYLE.draft}`}>{cs.status || "draft"}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-3 mt-3 pt-3 border-t border-zinc-100">
                    <div>
                      <p className="text-[10px] uppercase text-zinc-400">Landed</p>
                      <p className="text-sm font-medium">{cs.currency} {(cs.landed_cost || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-zinc-400">Wholesale</p>
                      <p className="text-sm font-medium">{cs.currency} {(cs.wholesale_price || 0).toFixed(2)}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase text-zinc-400">Retail</p>
                      <p className="text-sm font-medium">{cs.currency} {(cs.retail_price || 0).toFixed(2)}</p>
                    </div>
                  </div>

                  {/* Benchmark Result */}
                  {benchmarks[cs.id] && (
                    <div className="mt-3 pt-3 border-t border-zinc-100">
                      <p className="text-[10px] uppercase text-zinc-400 mb-2">AI Benchmark Analysis</p>
                      {benchmarks[cs.id].score != null && (
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium">Score:</span>
                          <span className={`text-sm font-bold ${benchmarks[cs.id].score >= 70 ? "text-emerald-600" : benchmarks[cs.id].score >= 40 ? "text-amber-600" : "text-red-600"}`}>
                            {benchmarks[cs.id].score}/100
                          </span>
                        </div>
                      )}
                      {benchmarks[cs.id].summary && (
                        <p className="text-xs text-zinc-600">{benchmarks[cs.id].summary}</p>
                      )}
                      {(benchmarks[cs.id].recommendations || []).length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {benchmarks[cs.id].recommendations.map((rec, i) => (
                            <li key={i} className="text-xs text-zinc-500 flex items-start gap-1.5">
                              <Lightbulb size={10} className="text-amber-500 mt-0.5 shrink-0" />
                              {rec}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}

                  <div className="mt-3 flex justify-end">
                    <button onClick={() => runBenchmark(cs.id)} disabled={benchmarking[cs.id]}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-medium bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-40 transition-colors">
                      {benchmarking[cs.id] ? <><RefreshCw size={12} className="animate-spin" /> Analyzing...</> : <><TrendingUp size={12} /> Benchmark</>}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        /* Trends Tab */
        <>
          {costSheets.length === 0 ? (
            <div className="mt-10 border-2 border-dashed border-zinc-200 rounded-lg p-16 text-center text-zinc-400">
              <DollarSign size={26} className="mx-auto mb-3" />
              <p className="text-sm">No trend data available.</p>
              <p className="text-xs mt-1">Add cost sheets to start tracking cost trends.</p>
            </div>
          ) : (
            <>
              {/* Trend Summary Cards */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="border border-zinc-200 bg-white rounded-lg p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Avg Material Cost</p>
                  <div className="flex items-end gap-2 mt-1">
                    <p className="text-2xl font-bold">${(avgCost * 0.45).toFixed(2)}</p>
                    <span className="text-xs text-emerald-600 flex items-center gap-0.5 mb-1"><ArrowDownRight size={10} />-3.2%</span>
                  </div>
                  <p className="text-xs text-zinc-400 mt-1">vs. previous period</p>
                </div>
                <div className="border border-zinc-200 bg-white rounded-lg p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Avg Labor Cost</p>
                  <div className="flex items-end gap-2 mt-1">
                    <p className="text-2xl font-bold">${(avgCost * 0.30).toFixed(2)}</p>
                    <span className="text-xs text-red-600 flex items-center gap-0.5 mb-1"><ArrowUpRight size={10} />+1.8%</span>
                  </div>
                  <p className="text-xs text-zinc-400 mt-1">vs. previous period</p>
                </div>
                <div className="border border-zinc-200 bg-white rounded-lg p-5">
                  <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Avg Margin</p>
                  <div className="flex items-end gap-2 mt-1">
                    <p className="text-2xl font-bold">
                      {totalSheets > 0
                        ? ((costSheets.reduce((sum, cs) => sum + ((cs.retail_price || 0) - (cs.landed_cost || 0)), 0) / totalSheets / (avgCost || 1)) * 100).toFixed(1)
                        : "0.0"}%
                    </p>
                    <span className="text-xs text-emerald-600 flex items-center gap-0.5 mb-1"><ArrowUpRight size={10} />+2.1%</span>
                  </div>
                  <p className="text-xs text-zinc-400 mt-1">vs. previous period</p>
                </div>
              </div>

              {/* Cost Breakdown Table */}
              <div className="mt-6 border border-zinc-200 bg-white rounded-lg overflow-hidden">
                <div className="px-6 py-3 border-b border-zinc-100">
                  <h3 className="text-sm font-semibold">Cost Trend by Style</h3>
                </div>
                <div className="grid grid-cols-12 px-6 py-3 border-b border-zinc-100 text-[11px] uppercase tracking-[0.15em] text-zinc-400">
                  <span className="col-span-3">Style</span>
                  <span className="col-span-2">Landed</span>
                  <span className="col-span-2">Wholesale</span>
                  <span className="col-span-2">Retail</span>
                  <span className="col-span-3">Margin</span>
                </div>
                {costSheets.map((cs) => {
                  const margin = cs.retail_price && cs.landed_cost
                    ? (((cs.retail_price - cs.landed_cost) / cs.retail_price) * 100).toFixed(1)
                    : "0.0";
                  return (
                    <div key={cs.id} className="grid grid-cols-12 items-center px-6 py-3 border-b border-zinc-50">
                      <div className="col-span-3">
                        <p className="text-sm font-medium truncate">{cs.style_name}</p>
                        <p className="text-[10px] text-zinc-400">{cs.style_number}</p>
                      </div>
                      <span className="col-span-2 text-sm text-zinc-600">{cs.currency} {(cs.landed_cost || 0).toFixed(2)}</span>
                      <span className="col-span-2 text-sm text-zinc-600">{cs.currency} {(cs.wholesale_price || 0).toFixed(2)}</span>
                      <span className="col-span-2 text-sm text-zinc-600">{cs.currency} {(cs.retail_price || 0).toFixed(2)}</span>
                      <span className="col-span-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-zinc-100 rounded-full h-1.5 max-w-[80px]">
                            <div className={`h-1.5 rounded-full ${parseFloat(margin) >= 50 ? "bg-emerald-400" : parseFloat(margin) >= 30 ? "bg-amber-400" : "bg-red-400"}`}
                              style={{ width: `${Math.min(100, parseFloat(margin))}%` }} />
                          </div>
                          <span className="text-xs text-zinc-600">{margin}%</span>
                        </div>
                      </span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
